import config from 'garaje/config/environment';
import Mirage from 'miragejs';

function createAssignments(schema, attributes) {
  const deskAssignments = [];
  if ('assignments' in attributes) {
    attributes['assignments']?.forEach((assignment) => {
      deskAssignments.push({
        'employee-id': assignment['employee-id'],
        'start-time': assignment['start-time'],
        'should-send-notification': assignment['should-send-notification'],
        'scheduled-by': assignment['scheduled-by'],
      });
    });
  }
  const createdAssignments = [];
  deskAssignments.forEach((it) => {
    createdAssignments.push(schema.assignments.create(it));
  });
  return createdAssignments;
}

export default function () {
  this.urlPrefix = config.rmsHost;

  // **************************************************************************
  // Announcements
  // **************************************************************************
  this.get('/a/rms/announcements');
  this.get('/a/rms/announcements/:id');
  this.post('/a/rms/announcements');
  this.patch('/a/rms/announcements/:id');
  this.delete('/a/rms/announcements/:id');
  this.post('/a/rms/announcements/:id', function () {
    return new Mirage.Response(204, {});
  });

  // **************************************************************************
  // Desks
  // **************************************************************************
  this.get('/a/rms/reservations', function (schema, request) {
    let reservations = schema.reservations.all();

    if (request.queryParams['filter[user-id]']) {
      reservations = schema.reservations.where({ userId: request.queryParams['filter[user-id]'] });
    }
    if (request.queryParams['filter[start-date]'] && request.queryParams['filter[end-date]']) {
      reservations = reservations.filter((res) => {
        const startTime = res.startTime;
        const endTime = res.endTime;

        return (
          startTime >= request.queryParams['filter[start-date]'] && endTime <= request.queryParams['filter[end-date]']
        );
      });
    }
    if (request.queryParams['filter[floor-id]']) {
      reservations = reservations.filter((res) => {
        return res.desk.floor.id === request.queryParams['filter[floor-id]'];
      });
    }
    const total = reservations.length;
    const response = this.serializerOrRegistry.serialize(reservations, request);
    if (request.queryParams['page[offset]']) {
      response.data.splice(0, request.queryParams['page[offset]']);
    }

    if (request.queryParams['page[limit]']) {
      response.data.splice(request.queryParams['page[limit]'], response.data.length);
    }

    if (request.queryParams['meta'] === 'reservation-count') {
      response.meta = { 'reservation-count': total };
    }

    return new Mirage.Response(200, {}, response);
  });

  this.post('a/rms/reservations');
  this.get('a/rms/reservations/:id');
  this.delete('a/rms/reservations/:id');

  this.post('/a/rms/bulk-reservations', function (schema, request) {
    const body = JSON.parse(request.requestBody);
    body.forEach((res) => {
      const relationships = res.data.relationships;
      const user = schema.users.find(relationships.user.data.id);
      const isPartialDay = res.data.attributes['end-time'] - res.data.attributes['start-time'] < 86399;
      const deskId = relationships.desk ? relationships.desk.data.id : schema.desks.first().id;
      schema.reservations.create({
        startTime: res.data.attributes['start-time'],
        endTime: res.data.attributes['end-time'],
        userEmail: user.email,
        isPartialDay,
        userId: relationships.user.data.id,
        deskId: deskId,
        inviteId: relationships.invite.data.id,
        locationId: relationships.location.data.id,
      });
    });
    return new Mirage.Response(200, {}, schema.reservations.all());
  });

  this.get('/a/rms/amenities');
  this.post('/a/rms/amenities');
  this.patch('/a/rms/amenities/:id');
  this.delete('/a/rms/amenities/:id');
  this.get('/a/rms/assignments/:id');

  this.get('/a/rms/floors', function (schema, { queryParams }) {
    const locationId = queryParams['filter[location-id]'];
    let floors = schema.floors.where({ locationId });

    const queryFilter = queryParams['filter[query]'];
    if (queryFilter) {
      floors = floors.filter(({ name }) => name.includes(queryFilter));
    }

    return floors;
  });
  this.patch('/a/rms/desks/:id', function (schema, request) {
    const body = JSON.parse(request.requestBody);
    const mapFloor = schema.mapFloors.find(body.data.relationships.floor.data.id);
    const areaMap = schema.areaMaps.find(mapFloor.areaMapId);
    const desk = schema.desks.find(body.data.id);
    let deskAmenities = [];
    if (body.data.relationships.amenities) {
      deskAmenities = body.data.relationships.amenities?.data.map((amenity) => schema.amenities.find(amenity.id));
    }
    const createdAssignments = createAssignments(schema, body.data.attributes);
    desk.update({
      name: body.data.attributes.name,
      enabled: body.data.attributes.enabled,
      assignedTo: body.data.attributes['assigned-to'],
      amenityIds: deskAmenities.map((amenity) => amenity.id),
      neighborhood: body.data.attributes.neighborhood,
      assignmentIds: createdAssignments.map((assignment) => assignment.id),
    });

    const mapFeature = schema.mapFeatures.findBy({ externalId: desk.id });

    if (!mapFeature) {
      schema.mapFeatures.create({
        name: body.data.attributes.name,
        enabled: true,
        type: 'desk',
        'external-id': desk.id,
        'parent-feature-id': null,
        notes: null,
        geometry: {
          type: 'Point',
          coordinates: [body.data.attributes['x-pos'], body.data.attributes['y-pos']],
        },
        mapFloor: mapFloor,
        areaMap: areaMap,
      });
    }
    const response = this.serializerOrRegistry.serialize(desk, request);
    return new Mirage.Response(200, {}, response);
  });
  this.delete('/a/rms/desks/:id', function (schema, request) {
    const desk = schema.desks.find(request.params.id);
    const mapFeature = schema.mapFeatures.findBy({ externalId: desk.id });
    mapFeature.destroy();
    desk.destroy();
    return new Mirage.Response(204, {});
  });
  this.get('/a/rms/desks', function (schema, request) {
    const floorId = request.queryParams['filter[floor-id]'];
    let desks = schema.desks.all();

    if (floorId) {
      desks = schema.desks.where({ floorId });
    }
    const queryFilter = request.queryParams['filter[query]'];
    if (queryFilter) {
      desks = desks.filter(({ name }) => name.includes(queryFilter));
    }

    const idFilter = request.queryParams['filter[id]'];
    if (idFilter) {
      const ids = idFilter.split(',');
      desks = desks.filter(({ id }) => ids.includes(id));
    }
    const total = desks.length;
    const response = this.serializerOrRegistry.serialize(desks, request);
    if (request.queryParams['page[offset]']) {
      response.data.splice(0, request.queryParams['page[offset]']);
    }

    if (request.queryParams['page[limit]']) {
      response.data.splice(request.queryParams['page[limit]'], desks.length);
    }
    response.meta = { total };
    return new Mirage.Response(200, {}, response);
  });

  this.post('a/rms/desks', function (schema, request) {
    const body = JSON.parse(request.requestBody);
    const floor = schema.floors.find(body.data.relationships.floor.data.id);
    const mapFloor = schema.mapFloors.find(body.data.relationships.floor.data.id);
    const areaMap = schema.areaMaps.find(mapFloor.areaMapId);
    const desk = schema.desks.create({
      name: body.data.attributes.name,
      enabled: body.data.attributes.enabled,
      floor: floor,
    });
    schema.mapFeatures.create({
      name: body.data.attributes.name,
      enabled: true,
      type: 'desk',
      'external-id': desk.id,
      'parent-feature-id': null,
      notes: null,
      geometry: {
        type: 'Point',
        coordinates: [body.data.attributes['x-pos'], body.data.attributes['y-pos']],
      },
      mapFloor: mapFloor,
      areaMap: areaMap,
    });
    const response = this.serializerOrRegistry.serialize(desk, request);
    return new Mirage.Response(201, {}, response);
  });

  this.post('/a/rms/available-desks', function (schema, request) {
    const body = JSON.parse(request.requestBody);
    const ranges = body['availability-ranges'];
    const response = ranges.map((range, idx) => ({
      'start-time': range['start-time'].toString(),
      'end-time': range['end-time'].toString(),
      availability: idx % 2 ? 'false' : 'true',
    }));
    return new Mirage.Response(200, {}, { availabilities: response });
  });

  this.post('/a/rms/desks/abstract/change-desk', function (schema, request) {
    const body = JSON.parse(request.requestBody);
    const attributes = body.data.attributes;
    const oldRes = schema.reservations.find(attributes['reservation-id']);
    const user = schema.users.find(oldRes.attrs.userId);
    const isPartialDay = attributes['end-time'] - attributes['start-time'] < 86399;
    const resObject = {
      createdAt: new Date(),
      deskId: attributes['new-desk-id'],
      inviteId: attributes['invite-id'],
      userId: oldRes.attrs.userId,
      locationId: oldRes.attrs.locationId,
      isPartialDay,
      startTime: attributes['start-time'],
      endTime: attributes['end-time'],
      userEmail: user.email,
    };
    oldRes.destroy();
    return schema.reservations.create(resObject);
  });

  this.get('/a/rms/neighborhoods/:id');
  this.patch('/a/rms/neighborhoods/:id');
  this.delete('/a/rms/neighborhoods/:id');
  this.post('/a/rms/neighborhoods');

  this.get('/a/rms/floors/:id');
  this.patch('/a/rms/floors/:id');
  this.delete('/a/rms/floors/:id');
  this.post('/a/rms/floors');

  this.post('/a/rms/v2/generics/resources/bulk-update', function (schema, request) {
    const desksToUpdate = JSON.parse(request.requestBody)['atomic:operations'];
    const createdAssignments = {};
    desksToUpdate.map((desk) => {
      const deskInfo = desk.data;
      const updatedDesk = schema.db.desks.update(deskInfo.id, {
        name: deskInfo.attributes.name,
        enabled: deskInfo.attributes.enabled,
        assignedTo: deskInfo.attributes['assigned-to'],
        xPos: deskInfo.attributes['x-pos'],
        yPos: deskInfo.attributes['y-pos'],
        amenityIds: deskInfo.relationships.amenities?.data.map((amenity) => amenity.id),
        draftId: deskInfo.relationships.draft?.data.id ?? null,
        neighborhoodId: deskInfo.relationships.neighborhood?.data.id ?? null,
        floorId: deskInfo.relationships.floor.data.id,
        locationId: deskInfo.relationships.location.data.id,
      });
      schema.db.mapFeatures.update({
        name: deskInfo.attributes.name,
        enabled: true,
      });
      createdAssignments[updatedDesk.id] = createAssignments(schema, deskInfo.attributes);
      return updatedDesk;
    });
    let includedAssignments = null;
    if (request.queryParams.include?.includes('assignments')) {
      includedAssignments = Object.keys(createdAssignments)
        .map((deskId) => {
          return createdAssignments[deskId].map((assignment) => {
            const assignmentResponse = {
              id: assignment.id,
              type: 'assignments',
              attributes: {
                'start-time': assignment.attrs['start-time'],
                'should-send-notification': assignment.attrs['should-send-notification'],
                'scheduled-by': assignment.attrs['scheduled-by'],
              },
              relationships: {
                resource: {
                  data: {
                    id: deskId,
                    type: 'resources',
                  },
                },
              },
            };
            if (assignment.attrs['employee-id']) {
              assignmentResponse.relationships['employee'] = {
                data: {
                  id: assignment.attrs['employee-id'],
                  type: 'employees',
                },
              };
            }
            return assignmentResponse;
          });
        })
        .flat();
    }

    const response = {
      data: desksToUpdate.map((desk) => {
        delete desk.data.relationships.location;
        const response = {
          id: desk.data.id,
          attributes: desk.data.attributes,
          relationships: desk.data.relationships,
        };
        if (createdAssignments[desk.data.id]) {
          response.relationships.assignments = { data: [] };
          createdAssignments[desk.data.id].forEach((assignment) => {
            response.relationships.assignments.data.push({
              id: assignment.id,
              type: 'assignments',
            });
          });
        }
        return response;
      }),
    };
    if (request.queryParams.include?.includes('assignments')) {
      response.included = includedAssignments;
    }
    return response;
  });

  this.post('/a/rms/v2/generics/resources/bulk-create', function (schema, request) {
    const body = JSON.parse(request.requestBody)['atomic:operations'];
    const createdAssignments = {};
    const desksToCreate = body.map((desk) => {
      const deskInfo = desk.data;
      const createdDesk = schema.desks.create({
        name: deskInfo.attributes.name,
        enabled: deskInfo.attributes.enabled,
        assignedTo: deskInfo.attributes['assigned-to'],
        xPos: deskInfo.attributes.geometry.coordinates[0],
        yPos: deskInfo.attributes.geometry.coordinates[1],
        amenityIds: deskInfo.relationships.amenities?.data.map((amenity) => amenity.id),
        draftId: deskInfo.relationships.draft?.data.id ?? null,
        neighborhoodId: deskInfo.relationships.neighborhood?.data?.id ?? null,
        floorId: deskInfo.relationships.floor.data.id,
        locationId: deskInfo.relationships.location.data.id,
      });
      createdAssignments[createdDesk.id] = createAssignments(schema, deskInfo.attributes);
      return createdDesk;
    });

    desksToCreate.forEach((desk) => {
      schema.mapFeatures.create({
        name: desk.name,
        enabled: true,
        type: 'desk',
        'external-id': desk.id,
        'parent-feature-id': null,
        notes: null,
        geometry: {
          type: 'Point',
          coordinates: [desk.xPos, desk.yPos],
        },
        mapFloor: schema.mapFloors.find(desk.floorId),
        areaMap: schema.areaMaps.find(schema.mapFloors.find(desk.floorId).areaMapId),
      });
    });

    let includedAssignments = null;
    if (request.queryParams.include?.includes('assignments')) {
      includedAssignments = Object.keys(createdAssignments)
        .map((deskId) => {
          return createdAssignments[deskId].map((assignment) => {
            const assignmentResponse = {
              id: assignment.id,
              type: 'assignments',
              attributes: {
                'start-time': assignment.attrs['start-time'],
                'should-send-notification': assignment.attrs['should-send-notification'],
                'scheduled-by': assignment.attrs['scheduled-by'],
              },
              relationships: {
                resource: {
                  data: {
                    id: deskId,
                    type: 'resources',
                  },
                },
              },
            };
            if (assignment.attrs['employee-id']) {
              assignmentResponse.relationships['employee'] = {
                data: {
                  id: assignment.attrs['employee-id'],
                  type: 'employees',
                },
              };
            }
            return assignmentResponse;
          });
        })
        .flat();
    }
    const response = {
      data: body.map((desk, idx) => {
        const createdDesk = desksToCreate[idx];
        const deskResponse = {
          id: createdDesk.id,
          attributes: desk.data.attributes,
          relationships: desk.data.relationships,
        };
        if (createdAssignments[createdDesk.id]) {
          deskResponse.relationships.assignments = { data: [] };
          createdAssignments[createdDesk.id].forEach((assignment) => {
            deskResponse.relationships.assignments.data.push({
              id: assignment.id,
              type: 'assignments',
            });
          });
        }
        return deskResponse;
      }),
    };
    if (request.queryParams.include?.includes('assignments')) {
      response.included = includedAssignments;
    }
    return response;
  });

  this.post('/a/rms/v2/generics/resources/bulk-delete', function (schema, request) {
    const desksToDelete = JSON.parse(request.requestBody)['atomic:operations'];
    desksToDelete.map((desk) => {
      schema.mapFeatures.findBy({ externalId: desk.data.id }).destroy();
      schema.desks.find(desk.data.id)?.destroy();
    });

    return new Mirage.Response(204);
  });

  this.post('/a/rms/desks/upload-desks-per-location', function () {
    return new Mirage.Response(204);
  });
  this.post('/a/rms/desks/floor-plan/:id', function () {
    return new Mirage.Response(204);
  });
  this.delete('/a/rms/floors/:id/floor-plan', function (schema, request) {
    schema.db.floors.update(request.params.id, {
      floorPlanUrl: null,
    });

    return new Mirage.Response(204);
  });
  this.get('/a/rms/neighborhoods', function (schema, { queryParams }) {
    const locationId = queryParams['filter[location-id]'];
    return schema.neighborhoods.where({ locationId });
  });

  this.post('/a/rms/neighborhoods');
  this.patch('/a/rms/neighborhoods/:id');
  this.delete('/a/rms/neighborhoods/:id');

  // **************************************************************************
  // Desk Locations
  // **************************************************************************
  this.get('/a/rms/desk-locations');
  this.post('/a/rms/desk-locations');
  this.get('/a/rms/desk-locations/:id');
  this.patch('/a/rms/desk-locations/:id');

  this.patch('/a/rms/workplace-days/:id');

  this.post('/a/rms/desks/bulk-desks-update', function () {
    return new Mirage.Response(204);
  });

  this.post('a/rms/desks/abstract/release-desk', function (schema, request) {
    const body = JSON.parse(request.requestBody);
    const attributes = body.data.attributes;
    const reservation = schema.reservations.find(attributes['reservation-id']);
    reservation.destroy();
    return new Mirage.Response(204);
  });

  this.post('/a/rms/desks/abstract/bulk-entry-check-in', function (_schema, _request) {
    return new Mirage.Response(204);
  });

  // **************************************************************************
  // Room Devices
  // **************************************************************************
  this.get('/a/rms/api/v2/room-devices');
  this.get('/a/rms/api/v2/room-devices/:id');
  this.patch('/a/rms/api/v2/room-devices/:id');
}
